import React, { useEffect, useState } from 'react';
import { Download, Archive, UserPlus, FolderOpen } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const STATUS_CONFIG = {
  'Rascunho': { color: 'bg-gray-500', label: 'Rascunho' },
  'Em Revisão': { color: 'bg-yellow-500', label: 'Em Revisão' },
  'Aprovado': { color: 'bg-blue-500', label: 'Aprovado' },
  'Finalizado': { color: 'bg-green-600', label: 'Finalizado' },
  'Arquivado': { color: 'bg-red-500', label: 'Arquivado' },
};

const FLOW_STEPS = ['Rascunho', 'Em Revisão', 'Aprovado', 'Finalizado', 'Arquivado'];

const DocumentSidebar = ({ doc, client, assignee, versions, history, documentId, onAssign, updatedByInfo, createdByInfo }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
  const [alfrescoExports, setAlfrescoExports] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/alfresco/exports/${documentId}`, { withCredentials: true })
      .then(res => setAlfrescoExports(res.data))
      .catch(() => {});
  }, [API_URL, documentId]);

  return (
    <div className="space-y-6">
      {/* Info */}
      <div className="bg-white border border-[#E5E5EA] rounded-xl p-5">
        <h3 className="font-semibold text-[#111111] mb-3">Informacoes</h3>
        <div className="space-y-3 text-sm">
          <div>
            <span className="text-[#6E6E73]">Criado em:</span>
            <p className="font-medium">{new Date(doc.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          {createdByInfo && (
            <div>
              <span className="text-[#6E6E73]">Criado por:</span>
              <p className="font-medium">{createdByInfo.name}</p>
            </div>
          )}
          <div>
            <span className="text-[#6E6E73]">Atualizado em:</span>
            <p className="font-medium">{new Date(doc.updated_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          {updatedByInfo && (
            <div>
              <span className="text-[#6E6E73]">Atualizado por:</span>
              <p className="font-medium">{updatedByInfo.name}</p>
            </div>
          )}
          <div>
            <span className="text-[#6E6E73]">Versao:</span>
            <p className="font-medium">v{doc.current_version}</p>
          </div>
        </div>
      </div>

      {/* Assignee */}
      <div className="bg-white border border-[#E5E5EA] rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[#111111]">Responsavel</h3>
          {doc.status !== 'Arquivado' && (
            <Button variant="ghost" size="sm" className="h-7 text-xs text-[#AF2628] hover:text-[#961B22]" onClick={onAssign} data-testid="assign-button">
              <UserPlus className="w-3.5 h-3.5 mr-1" />{assignee ? 'Alterar' : 'Atribuir'}
            </Button>
          )}
        </div>
        {assignee ? (
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#AF2628] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
              {assignee.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="min-w-0">
              <p className="font-medium text-sm text-[#111111] truncate">{assignee.name}</p>
              <p className="text-xs text-[#6E6E73] truncate">{assignee.role}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#AEAEB2]">Nenhum responsavel atribuido</p>
        )}
      </div>

      {/* Approval Flow */}
      <div className="bg-white border border-[#E5E5EA] rounded-xl p-5">
        <h3 className="font-semibold text-[#111111] mb-3">Fluxo de Aprovacao</h3>
        <div className="space-y-2">
          {FLOW_STEPS.map((s, i) => {
            const isActive = s === doc.status;
            const isPast = FLOW_STEPS.indexOf(s) < FLOW_STEPS.indexOf(doc.status);
            const cfg = STATUS_CONFIG[s] || { color: 'bg-gray-300', label: s };
            return (
              <div key={s} className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${isActive ? cfg.color : isPast ? 'bg-green-400' : 'bg-gray-200'}`} />
                <span className={`text-sm ${isActive ? 'font-semibold text-[#111111]' : isPast ? 'text-[#6E6E73] line-through' : 'text-[#AEAEB2]'}`}>{cfg.label}</span>
                {i < FLOW_STEPS.length - 1 && <div className="flex-1 border-t border-dashed border-[#E5E5EA]" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Versions */}
      <div className="bg-white border border-[#E5E5EA] rounded-xl p-5">
        <h3 className="font-semibold text-[#111111] mb-3">Versoes do Documento</h3>
        {versions.length > 0 ? (
          <div className="space-y-3">
            {versions.map((v) => (
              <div key={v.version_id} className="text-sm border-l-2 border-[#2B5C8A] pl-3 py-1">
                <div className="flex items-center justify-between">
                  <p className="font-medium">v{v.version_number}</p>
                  {v.has_file && (
                    <a href={`${API_URL}/documents/${documentId}/versions/${v.version_number}/download`} className="text-[#AF2628] hover:text-[#961B22] text-xs font-medium flex items-center gap-1" data-testid={`download-version-${v.version_number}`}>
                      <Download className="w-3 h-3" /> Baixar
                    </a>
                  )}
                </div>
                {v.source === 'upload_externo' && <p className="text-xs text-[#2B5C8A] font-medium">Upload externo</p>}
                {v.file_name && <p className="text-xs text-[#6E6E73] truncate">{v.file_name}</p>}
                <p className="text-xs text-[#6E6E73]">{v.created_by_name}</p>
                <p className="text-xs text-[#AEAEB2]">{new Date(v.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#AEAEB2]">Nenhuma versao registrada</p>
        )}
      </div>

      {/* Alfresco Exports */}
      {alfrescoExports.length > 0 && (
        <div className="bg-white border border-[#E5E5EA] rounded-xl p-5" data-testid="alfresco-exports-sidebar">
          <h3 className="font-semibold text-[#111111] mb-3 flex items-center gap-2">
            <FolderOpen className="w-4 h-4 text-[#AF2628]" />
            Alfresco
          </h3>
          <div className="space-y-2">
            {alfrescoExports.slice(0, 5).map(exp => (
              <div key={exp.export_id} className="text-sm border-l-2 border-[#AF2628] pl-3 py-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-xs text-[#AF2628] uppercase">{exp.format}</span>
                  <span className="text-xs text-[#6E6E73]">v{exp.version}</span>
                  {exp.auto_export && <span className="bg-blue-100 text-blue-700 px-1 py-0.5 rounded text-[10px]">Auto</span>}
                </div>
                <p className="text-xs text-[#6E6E73]">{exp.exported_by_name}</p>
                <p className="text-xs text-[#AEAEB2]">{new Date(exp.exported_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-[#E5E5EA] rounded-xl p-5">
        <h3 className="font-semibold text-[#111111] mb-3">Historico</h3>
        {history.length > 0 ? (
          <div className="space-y-3">
            {history.map((h, idx) => (
              <div key={`${h.changed_at}-${idx}`} className={`text-sm border-l-2 ${h.event_type === 'assignment' ? 'border-[#2B5C8A]' : 'border-[#AF2628]'} pl-3 py-1`}>
                {h.event_type === 'assignment' ? (
                  <p className="font-medium text-[#2B5C8A]">{h.comment}</p>
                ) : (
                  <p className="font-medium">{h.from_status} &rarr; {h.to_status}</p>
                )}
                <p className="text-xs text-[#6E6E73]">{h.changed_by_name}</p>
                {h.comment && h.event_type !== 'assignment' && <p className="text-xs text-[#6E6E73] italic mt-0.5">"{h.comment}"</p>}
                <p className="text-xs text-[#AEAEB2]">{new Date(h.changed_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#AEAEB2]">Nenhuma alteracao registrada</p>
        )}
      </div>
    </div>
  );
};

export default DocumentSidebar;
